import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminBlogsTab = ({ blogs, fetchBlogs, authHeaders, showMsg, loading, setLoading, UploadButton, handleFormImageUpload }) => {
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', category: '', image: '', author: "Nistala's Group" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/blogs`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      if (res.ok) { setForm({ title: '', excerpt: '', content: '', category: '', image: '', author: "Nistala's Group" }); fetchBlogs(); showMsg('Blog post published!'); }
      else { const d = await res.json(); showMsg(d.detail || 'Failed to create blog', 'error'); }
    } catch { showMsg('Error creating blog post', 'error'); }
    setLoading(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1 shadow-sm border border-[#E8DFD3] h-fit bg-white">
        <CardContent className="p-5">
          <h3 className="font-bold text-[#2C1810] mb-4 flex items-center gap-2">
            <Plus size={16} className="text-[#5C7A3A]" /> New Blog Post
          </h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#7a6555] mb-1 block">Title</label>
              <Input data-testid="blog-title-input" placeholder="Blog post title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
            </div>
            <div>
              <label className="text-xs font-medium text-[#7a6555] mb-1 block">Category</label>
              <Input data-testid="blog-category-input" list="blog-categories-list" placeholder="e.g., Agriculture, Sustainability" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} required />
              <datalist id="blog-categories-list">
                {[...new Set(blogs.map(b => b.category).filter(Boolean))].map(c => <option key={c} value={c} />)}
                <option value="Agriculture" />
                <option value="Sustainability" />
                <option value="Technology" />
                <option value="Empowerment" />
                <option value="Organic" />
                <option value="Logistics" />
              </datalist>
            </div>
            <div>
              <label className="text-xs font-medium text-[#7a6555] mb-1 block">Excerpt</label>
              <Textarea data-testid="blog-excerpt-input" placeholder="Short summary shown on blog listing" value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} required rows={2} />
            </div>
            <UploadButton currentUrl={form.image} onUpload={(file) => handleFormImageUpload(file, setForm, 'image')} label="Upload Cover Image" />
            <div>
              <label className="text-xs font-medium text-[#7a6555] mb-1 block">Author</label>
              <Input data-testid="blog-author-input" placeholder="Author name" value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} required />
            </div>
            <div>
              <label className="text-xs font-medium text-[#7a6555] mb-1 block">Content</label>
              <Textarea data-testid="blog-content-input" placeholder="Full article content. Use ## for headings, **bold** for emphasis, and blank lines for paragraphs." value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required rows={8} className="font-mono text-xs" />
            </div>
            <Button type="submit" data-testid="publish-blog-btn" disabled={loading || !form.image} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">{loading ? 'Publishing...' : 'Publish Blog Post'}</Button>
          </form>
        </CardContent>
      </Card>
      <div className="lg:col-span-2 space-y-3">
        {blogs.length === 0
          ? <div className="text-center py-12 text-[#7a6555]" data-testid="no-blogs">No blog posts yet. Write your first article.</div>
          : blogs.map(post => (
            <Card key={post.id} className="shadow-sm border border-[#E8DFD3] bg-white" data-testid={`blog-item-${post.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <img src={post.image} alt={post.title} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#2C1810] truncate">{post.title}</p>
                  <p className="text-xs text-[#7a6555]">{post.category} &middot; {post.author}</p>
                  <p className="text-xs text-[#7a6555] truncate mt-0.5">{post.excerpt}</p>
                </div>
                <Button onClick={async () => {
                  if (!window.confirm('Delete this blog post?')) return;
                  setLoading(true);
                  try {
                    await fetch(`${API}/api/admin/blogs/${post.id}`, { method: 'DELETE', headers: authHeaders() });
                    fetchBlogs(); showMsg('Blog deleted');
                  } catch { showMsg('Delete failed', 'error'); }
                  setLoading(false);
                }} variant="ghost" size="sm" data-testid={`delete-blog-${post.id}`} className="text-red-500 hover:bg-red-50 cursor-pointer"><Trash2 size={16} /></Button>
              </CardContent>
            </Card>
          ))
        }
      </div>
    </div>
  );
};

export default AdminBlogsTab;
